import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Check, Droplets, KeyRound, Zap, Clock, MapPin, CreditCard, HelpCircle, ShieldCheck, Timer, Receipt, BadgeCheck } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { ADS, CHARENTE, CHARENTE_REVIEWS } from "../lib/ads";
import { PAYMENTS } from "../lib/prestations";
import { COMPANY } from "../lib/data";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import BrandMarquee from "../components/BrandMarquee";
import GoogleReviews from "../components/GoogleReviews";
import QuoteForm from "../components/QuoteForm";

const ICONS = { Droplets, KeyRound, Zap };
const ENGAGEMENTS = [
  { Icon: Timer, t: "Rapidité", d: "Sur place au plus vite, 24h/24 et 7j/7." },
  { Icon: Receipt, t: "Devis clair", d: "Tarif annoncé avant, sans surprise." },
  { Icon: ShieldCheck, t: "Artisans assurés", d: "Professionnels certifiés et assurés." },
  { Icon: BadgeCheck, t: "Travail garanti", d: "Pièces et main-d'œuvre garanties." },
];

export default function AdsLandingPage({ metier }) {
  const cfg = ADS[metier];
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, [metier]);
  useEffect(() => { if (!cfg) navigate("/"); }, [cfg, navigate]);
  if (!cfg) return null;
  const Icon = ICONS[cfg.icon] || Droplets;

  return (
    <main data-testid={`ads-${metier}`} className="bg-white">
      <Seo path={cfg.path} title={cfg.title.replace(" — ", " ").slice(0, 60)} description={cfg.metaDescription} image={cfg.image} />

      {/* Hero */}
      <section className="relative pt-28 md:pt-32 pb-14 md:pb-20 overflow-hidden bg-slate-50 border-b border-slate-200">
        <div className="pointer-events-none absolute -top-16 right-0 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <span className="chip"><Icon className="h-4 w-4" /> {CHARENTE.name} · 24h/24 · 7j/7</span>
            <h1 className="mt-4 font-display font-extrabold text-ink text-4xl md:text-6xl leading-[1.05]">
              {cfg.h1} <span className="gradient-text">en Charente (16)</span>
            </h1>
            <p className="mt-5 text-lg text-ink-soft leading-relaxed max-w-xl">{cfg.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={COMPANY.phoneHref} data-testid="ads-call-btn" className="btn-primary text-base">
                <Phone className="h-5 w-5" /> {COMPANY.phone}
              </a>
              <a href="#devis" className="btn-outline">Devis gratuit</a>
            </div>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {["Intervention 24h/24", "Devis gratuit & sans surprise", "Artisans certifiés & assurés", "Toute la Charente (16)"].map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-ink-soft font-medium">
                  <span className="h-5 w-5 rounded-full bg-brand-green/15 flex items-center justify-center">
                    <Check className="h-3 w-3 text-brand-green" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[1.75rem] overflow-hidden border border-slate-200 shadow-xl shadow-slate-200/60 aspect-[4/3]"
          >
            <img src={cfg.image} alt={`${cfg.metier} en Charente`} fetchPriority="high" className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>

      <BrandMarquee category={cfg.category} />

      {/* Prestations + form */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal><span className="overline">Nos interventions</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-3 font-display font-extrabold text-ink text-3xl md:text-4xl">
                Un {cfg.metier.toLowerCase()} près de chez vous en Charente
              </h2>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {cfg.points.map((pt, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="card-soft flex items-start gap-4 p-5 hover:translate-y-0">
                    <span className="h-8 w-8 rounded-lg bg-brand-green/10 flex items-center justify-center shrink-0">
                      <Check className="h-4 w-4 text-brand-green" />
                    </span>
                    <span className="text-ink font-medium">{pt}</span>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Villes */}
            <Reveal delay={0.1}>
              <h3 className="mt-12 font-display font-bold text-ink text-2xl flex items-center gap-2">
                <MapPin className="h-6 w-6 text-brand" /> Villes desservies en Charente (16)
              </h3>
            </Reveal>
            <div className="mt-5 flex flex-wrap gap-2">
              {CHARENTE.cities.map((c) => (<span key={c} className="chip">{c}</span>))}
            </div>

            {/* Engagements */}
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ENGAGEMENTS.map((e, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="card-soft p-5 h-full hover:translate-y-0">
                    <span className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center">
                      <e.Icon className="h-5 w-5 text-brand" />
                    </span>
                    <h4 className="font-display font-bold text-ink mt-3">{e.t}</h4>
                    <p className="text-ink-soft text-sm mt-1">{e.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Sidebar form */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="card-soft p-7 hover:translate-y-0 text-center">
                <p className="overline">Appelez immédiatement</p>
                <a href={COMPANY.phoneHref} className="block font-display font-extrabold text-ink text-3xl mt-2 hover:text-brand transition-colors">{COMPANY.phone}</a>
                <a href={COMPANY.phoneHref} className="btn-primary w-full mt-4"><Phone className="h-4 w-4" /> Appeler maintenant</a>
              </div>
              <div id="devis" className="card-soft p-6 md:p-7 hover:translate-y-0 scroll-mt-24">
                <h3 className="font-display font-bold text-ink text-xl mb-4">Demander un devis gratuit</h3>
                <QuoteForm defaultService={cfg.category} source={`ads-charente-${metier}`} />
              </div>
            </div>
          </aside>
        </div>
      </section>

      <div className="bg-slate-50 border-y border-slate-200">
        <GoogleReviews reviews={CHARENTE_REVIEWS[cfg.category]} title="Avis de nos clients" subtitle="Des interventions notées 5 étoiles." />
      </div>

      {/* Map + infos */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="rounded-2xl overflow-hidden border border-slate-200 min-h-[320px]">
            <iframe title="Charente" src={CHARENTE.mapEmbed} width="100%" height="100%" style={{ border: 0, minHeight: "320px", display: "block" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
          <div className="grid sm:grid-cols-1 gap-5">
            <div className="card-soft p-6 hover:translate-y-0">
              <span className="h-11 w-11 rounded-xl bg-brand/10 flex items-center justify-center"><Clock className="h-5 w-5 text-brand" /></span>
              <h3 className="font-display font-bold text-ink text-lg mt-3">Horaires</h3>
              <p className="text-ink-soft">Ouvert 24h/24 et 7j/7 — jours fériés inclus</p>
            </div>
            <div className="card-soft p-6 hover:translate-y-0">
              <span className="h-11 w-11 rounded-xl bg-brand/10 flex items-center justify-center"><CreditCard className="h-5 w-5 text-brand" /></span>
              <h3 className="font-display font-bold text-ink text-lg mt-3">Moyens de paiement</h3>
              <div className="flex flex-wrap gap-2 mt-3">{PAYMENTS.map((p) => (<span key={p} className="chip">{p}</span>))}</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <h2 className="text-center font-display font-extrabold text-ink text-3xl md:text-4xl flex items-center justify-center gap-2">
            <HelpCircle className="h-7 w-7 text-brand" /> Questions fréquentes
          </h2>
          <Accordion type="single" collapsible className="w-full mt-8 space-y-3">
            {cfg.faq.map((item, i) => (
              <AccordionItem key={i} value={`f-${i}`} data-testid={`faq-item-${i}`} className="card-soft border border-slate-200 px-6 hover:translate-y-0">
                <AccordionTrigger className="font-display font-bold text-ink text-base md:text-lg text-left hover:no-underline py-5 [&>svg]:text-brand">{item.q}</AccordionTrigger>
                <AccordionContent className="text-ink-soft text-base leading-relaxed pb-5">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <a href={COMPANY.phoneHref} className="btn-primary text-base"><Phone className="h-5 w-5" /> {COMPANY.phone}</a>
          </div>
        </div>
      </section>
    </main>
  );
}
